import { CLIENT_AUTHENTICATION_SERVICE, ClientAuthenticationService } from "@cervval.js/client-auth/src/clientAuthentication.service";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { getElementByRef } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LoginData, User } from "@cervval.js/common-auth/src/loginData.model";
import { PERMISSION_SERVICE, PermissionService } from "@cervval.js/common-permissions";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { CollectionTools, UrlTools } from "@cervval.js/common/tools";
import { DefaultMenuHelper, MenuHelper } from "./menu/menu.helper";
import { ENavigationLifeCycle, LifeCycleEvent, NavigationListeners } from "./navigation.lifeCycle";
import { BasePageName, Page } from "./navigation.page";
import { DEFAULT_NAVIGATION_CONTAINER_REF, NAVIGATION_SERVICE, NavigationParameters, NavigationService, NavigationServiceConfiguration, PageDefinitions, SearchParams } from "./navigation.service";
/**
 * Navigation internal wrapper to ease page manipulation
 */
export interface PageWrapper<BasePageName> {

    pageName: BasePageName;

    /**
     * Promise used to create the Page instance
     */
    importPromise: () => Promise<Page>;

    /**
     * The html file content
     * Use htmlContent or pageFilePath
     */
    htmlElement?: HTMLElement;

    /**
     * Instance of the page - Used to make function calls, and updates
     */
    pageInstance?: Page;
}


/**
 * Parameters to install the navigation service
 */
export interface HashNavigationServiceSettings<BasePageName, PermissionContext = void> {

    /**
     * Default page for navigation
     */
    defaultPage?: BasePageName;

    /**
     * Default error page for navigation
     */
    defaultErrorPage?: BasePageName;

    /**
     * Optional: Custom container ref key to insert pages routing (by default pagesContainer is used)
     */
    navigationContainerRefKey: string;

    /**
     * Pages to register in navigationService
     */
    pages: PageDefinitions;

    /** All config pages (use to rebuild menu dynamically) */
    allConfigPages: PageDefinitions;

    /**
     * MenuHelper manages the menus
     */
    menuHelper: MenuHelper<BasePageName, PermissionContext>;

    /** Callback to get permission context when building menus */
    getterPermissionContext?: () => PermissionContext | undefined;

    /** If true, pages are removed from dom, otherwise they are simply hidden */
    removeFromDomOnPageHide?: boolean;
}


/**
 * NavigationService class using hash
 * Handles navigation between multiple pages in a single page application
 */
@Injectable(NAVIGATION_SERVICE)
export class HashNavigationService<PageName extends BasePageName, PermissionContext> implements NavigationService<PageName, PermissionContext> {

    @Injected(LOG_SERVICE)
    protected readonly _log!: LogService;

    @Injected(CLIENT_AUTHENTICATION_SERVICE, { optional: true })
    protected _clientAuthService?: ClientAuthenticationService;

    @Injected(PERMISSION_SERVICE)
    protected readonly _permissionService!: PermissionService<LoginData<User>, PermissionContext>;

    /** Before page show event triggered before page lifecycle event */
    @Event()
    public beforePageShow!: EventHandler<{ pageName: PageName }>;

    /** After page show event triggered after page lifecycle event */
    @Event()
    public afterPageShow!: EventHandler<{ pageName: PageName }>;

    /** Before navigation event */
    @Event()
    public beforeNavigation!: EventHandler<{ previous: PageName | undefined, next: PageName }>;

    /**
     * Service global settings
     */
    protected _settings?: HashNavigationServiceSettings<PageName, PermissionContext>;

    /**
     * List of registered pages used for navigation service
     */
    protected _navigationPages: Map<PageName, PageWrapper<PageName>> = new Map();

    /** @inheritdoc */
    public get pageNames(): PageName[] {
        return CollectionTools.getMapKeys(this._navigationPages);
    }

    /**
     * Get current navigation location hash page value (does not start with #)
     * @description CurrentHash can be different from currentPage, due to the fact that client can ask for an inexistent page,
     * so the hash(currentHash) will change, but not the displayed page(currentPage)
     * Always call the window location to set our current hash
     */
    public get currentHash(): PageName {

        const currentHash = window.location.hash;
        return this.getPageNameFromHash(currentHash);
    }

    /** @inheritdoc */
    public currentPage: PageName | undefined;

    /**
     * Used to store the params given to the uri
     * Filled automatically when params are found in the uri
     * 
     * Reminder: uri parameters are build like the following example
     * http://www.myWebsite.com?param1=value1&param2=value2 (...)
     * 
     * Then, everything after the ' ? ' will be handled as parameters
     * 
     */
    public queryParams: Record<string, string> = {};

    /**
     * Current "page routing container" Html object
     */
    private _navigationContainerHtml: HTMLElement | null = null;

    /** @inheritdoc */
    public configure(config: NavigationServiceConfiguration<PageName, PermissionContext>): void {
        if (this._settings) {
            throw new Error("Configuration was already done");
        }

        // initialize service settings
        this._settings = {
            pages: {},
            allConfigPages: { ...config.pages },
            defaultPage: config.defaultPage,
            defaultErrorPage: config.defaultErrorPage,
            navigationContainerRefKey: config.navigationContainerRef ?? DEFAULT_NAVIGATION_CONTAINER_REF,
            menuHelper: config.menuHelper ?? new DefaultMenuHelper(),
            getterPermissionContext: config.getterPermissionContext ? config.getterPermissionContext : undefined
        };

        // Configure menu helper
        this._settings.menuHelper.configure(document.body, config.menus, config.menuFactories);

        // Add pages to the navigation service
        if (config.pages) {
            this.addPages(config.pages);
        }

        // Subscribe to window url hash change, used to emit event in app and navigate
        this._subscribeToHashChange();

        // Display page for current hash or default page
        this._displayInitPage();
    }

    /** @inheritdoc */
    public getUrl(pageName: PageName, params?: NavigationParameters): string {
        return "#" + pageName; // TODO - Add navigation parameters to url
    }

    /** @inheritdoc */
    public getSearchParams(): SearchParams {
        // Create an object to parse and manage the search params in current URL
        const urlParams = new URLSearchParams(window.location.search);

        // Loop through the URL parameters and store them in searchParams
        const searchParams: SearchParams = {};
        urlParams.forEach((value, key) => searchParams[key] = value);
        return searchParams;
    }

    /**
     * Update url with deletion of specified params
     */
    public deleteSearchParams(...keys: string[]): void {
        // Create a new URL object based on the current URL
        const url = new URL(window.location.href);
        // Loop through the provided keys (search param names) to delete them
        for (const key of keys) {
            url.searchParams.delete(key);
        }
        // Update url without triggering a page reload
        window.history.replaceState({}, "", url);
    }


    /**
     * Return the page name included in the hash
     * Exemples: 
     *      classic: #MyPage => MyPage
     *      with params:#MyPage?param1=value1 => MyPage
     * @param hash 
     * @returns 
     */
    public getPageNameFromHash(hash: string): PageName {
        const splitted = hash.split("?");
        if (splitted.length > 1) {
            // If we open a new window with some params in the uri, the current hash is with all the params, so we need to remove them first
            return this._asPageName(splitted[0].substring(1));
        } else {
            return this._asPageName(hash.substring(1));
        }
    }

    /** @inheritdoc */
    public navigate(pageName: PageName, params?: NavigationParameters): void {

        const canNavigate = true; // TODO - Implements user authorization handlers

        if (canNavigate) {
            window.location.hash = "#" + pageName; // Navigate

            // TODO - Add navigation parameters to url
        } else {
            // If the user is not authorized to navigate, log an error (when authorization implemented)
            this._log.error(`User not authorized to navigate to page: ${pageName}`);
        }
    }

    /** @inheritdoc */
    public navigatePrevious(): void {
        window.history.back();
    }

    /** @inheritdoc */
    public navigateNext(): void {
        window.history.forward();
    }

    /** @inheritdoc */
    public addPages(pages: PageDefinitions): void {
        if (!this._settings) {
            this._log.error("NavigationService should be configured prior to adding pages (=> menus will not be handled)");
        }

        const allowedPages: PageDefinitions = this.filterPagesFromPermissions({ ...pages });

        const wrappers = this._wrapPages(allowedPages);
        wrappers.forEach((pageWrapper: PageWrapper<PageName>) => {
            this._addPage(pageWrapper);
        });

        // insert pages in menus
        if (this._settings?.menuHelper) {
            this._settings.menuHelper.setupMenus(this, this._log, allowedPages);
        }
    }

    /** 
     * @inheritdoc 
     * * this function modifies pages
     */
    public filterPagesFromPermissions(pages: PageDefinitions): PageDefinitions {
        const loginData: LoginData<User> | undefined = this._clientAuthService?.getLoginData();
        const context = this._settings?.getterPermissionContext ? this._settings.getterPermissionContext() : undefined;
        for (const key of Object.keys(pages)) {
            const permissions = getVOFValue(pages[key].permissions);
            if (permissions?.length) {
                if (!loginData) {
                    // If there is no loginData and page needs a permission, page is not reachable
                    delete pages[key];
                    continue;
                }
                let hasAllPermissions = true;
                for (const permStringOrArray of permissions) {
                    let hasSinglePermission = false;
                    if (Array.isArray(permStringOrArray)) {
                        // permStringOrArray is an array, permission is granted if user has at least one the permissions in the array
                        for (const permString of permStringOrArray) {
                            if (this._permissionService.hasPermission(loginData, permString, context)) {
                                hasSinglePermission = true;
                                break;
                            }
                        }
                    } else {
                        // permStringOrArray is a single permission
                        hasSinglePermission = this._permissionService.hasPermission(loginData, permStringOrArray, context);
                    }
                    if (!hasSinglePermission) {
                        // user needs to have all permissions within permissions, this one is not valid => we stop here
                        hasAllPermissions = false;
                        break;
                    }
                }
                if (!hasAllPermissions) {
                    // If loggedIn user does not have permission to see the page we remove it from the record
                    delete pages[key];
                }
            }
        }
        return pages;
    }


    /**
     * Add page to the navigation service
     *
     * @param page - PageWrapper to add to registered pages
     */
    private _addPage(page: PageWrapper<PageName>): Map<PageName, PageWrapper<PageName>> {

        // Check if the pageKey isn't already added to navigation pages
        if (!this._navigationPages.has(page.pageName)) {
            this._navigationPages.set(page.pageName, page);
        } else {
            this._log.error(`This page : ${page.pageName}, already exists`);
        }

        return this._navigationPages;
    }

    /** @inheritdoc */
    public refreshCurrentPage(): void {
        if (this.currentPage) {
            this._performNavigation(this.currentPage);
        }
    }

    /**
     * Show a page
     * @param pageWrapper - Page to show
     */
    protected _showPage(pageWrapper: PageWrapper<PageName>): void {
        // If the page is already loaded in app, append it to the body
        if (pageWrapper.htmlElement) {

            if (this._settings?.removeFromDomOnPageHide) {
                // Ensure navigation container is always defined
                if (!this._navigationContainerHtml && this._settings) {
                    // Retrieve the html container in DOM
                    this._navigationContainerHtml = getElementByRef(document.body, this._settings.navigationContainerRefKey);
                }

                // Show page html
                // Append page content into navigation container
                this._navigationContainerHtml?.append(pageWrapper.htmlElement);
            } else {
                // Show page html (remove style.display = "none")
                pageWrapper.htmlElement.style.removeProperty("display");
            }

        } else { // If page isn't loaded, load the chunk first, then show it
            this._loadPage(pageWrapper);
        }

    }

    /**
     * Hide a page
     * @param pageWrapper - Page to hide
     */
    protected _hidePage(pageWrapper: PageWrapper<PageName>): void {
        if (pageWrapper.htmlElement) {
            if (this._settings?.removeFromDomOnPageHide) {
                // Hide page element, remove it from DOM
                pageWrapper.htmlElement.remove();
            } else {
                // Hide page element
                pageWrapper.htmlElement.style.display = "none";
            }
        }
    }

    /**
     * Get page wrapper from navigationPages Map
     *
     * @param pageName - pageName selector
     */
    protected _getPageWrapper(pageName: PageName): PageWrapper<PageName> | undefined {
        if (pageName && String(pageName) !== "") {
            const pageWrapper: PageWrapper<PageName> | undefined = this._navigationPages.get(pageName);
            if (pageWrapper) {
                return pageWrapper;
            } else {
                this._log.error(`PageName: "${pageName}" not found in registered pages`);
            }
        }
        return undefined;
    }


    /**
     * Split the params from the uri, and fill the queryParams
     * ! Does not follow the conventional uri params writting
     * => conventional: domain?params#hash
     * => cjs: domain#hash?params
     * @see {@link https://developer.mozilla.org/en-US/docs/Learn/Common_questions/Web_mechanics/What_is_a_URL}
     */
    protected _processUrlParams(): void {
        // We use hash to ignore the conventional params, so when we have an uri with the following pattern domain?params#hash?params , it will ignore the first ?params
        const uri = window.location.hash;
        const splitted = uri.split("?"); // Split the params from the hash
        this.queryParams = {};
        if (splitted[1]) {// if params are found
            const params = splitted[1].split("&");// split sub params
            for (const param of params) {
                const splittedParam = param.split("=");// build the key-value association
                if (splittedParam.length === 2) {
                    this.queryParams[splittedParam[0]] = decodeURIComponent(splittedParam[1]);
                } else {
                    this._log.error(`Incorrect URI params: ${param}`);
                }
            }
        }
    }

    /**
     * Get the page instance a selected pageName - use the registered page names
     * @param pageName - pageName selector
     */
    private _getPageInstance(pageName: PageName): Page | undefined {
        const pageWrapper: PageWrapper<PageName> | undefined = this._getPageWrapper(pageName);
        if (pageWrapper?.pageInstance) { // Check if page instance attribute exist for this pageWrapper
            return pageWrapper.pageInstance;
        } else if (!pageWrapper) {
            this._log.error(`Page instance for pageName: "${pageName}" not found in navigationPages list`);
            return undefined;
        } else {
            return undefined;
        }
    }

    /** Subscribe to window onhashchange callback method */
    protected _subscribeToHashChange(): void {
        if (!("onhashchange" in window)) {
            throw new Error("hashchange not handled by browser");
        }

        // Set onHashChangeCallBack
        window.onhashchange = this._onLocationHashChanged.bind(this);
    }

    /**
     * When the window location hash has change
     *
     * @param event - Event returned by the "window.onhashchange" function
     */
    protected _onLocationHashChanged(event: HashChangeEvent): void {
        // Get the 'to' url, to dispatch events and navigate
        const toPageName: PageName = this._asPageName(UrlTools.getHashFromUrl(event.newURL));
        this._performNavigation(toPageName);
    }

    /**
     * perform navigation to page (and redirect to error page if needed)
     */
    protected _performNavigation(toPageName: PageName): void {
        // consider empty hash as the default page
        if (toPageName === "" && this._settings?.defaultPage) {
            toPageName = this._settings.defaultPage;
        }

        this.beforeNavigation.emit({ previous: this.currentPage, next: toPageName });

        // Fill queryParams if there is params in the uri
        this._processUrlParams();
        // Get the page destination(to) in registered pages
        const toPageWrapper: PageWrapper<PageName> | undefined = this._getPageWrapper(toPageName);

        // If the destination page is registered
        if (toPageWrapper) {

            // Get the current page
            const fromPageWrapper: PageWrapper<PageName> | undefined = this.currentPage ? this._getPageWrapper(this.currentPage) : undefined;

            // If this page is a registered one, hide it and trigger events
            if (fromPageWrapper) {
                this._dispatchLifeCycleEvent(fromPageWrapper.pageName, ENavigationLifeCycle.BeforePageHide, toPageWrapper.pageName);
                this._hidePage(fromPageWrapper);
                this._dispatchLifeCycleEvent(fromPageWrapper.pageName, ENavigationLifeCycle.AfterPageHide, toPageWrapper.pageName);
            }

            //  After hiding the previous page, display the destination page, update menus and trigger events
            this.beforePageShow.emit({ pageName: toPageWrapper.pageName });
            this._dispatchLifeCycleEvent(toPageWrapper.pageName, ENavigationLifeCycle.BeforePageShow, toPageWrapper.pageName);
            if (this._settings?.menuHelper) {
                this._settings.menuHelper.updateMenus(this, this._log, { previousPage: fromPageWrapper?.pageName, newPage: toPageWrapper.pageName });
            }
            this.currentPage = toPageWrapper.pageName;
            this._showPage(toPageWrapper);

            // Dispatch afterPageShow
            this._dispatchLifeCycleEvent(toPageWrapper.pageName, ENavigationLifeCycle.AfterPageShow, toPageWrapper.pageName);
            this.afterPageShow.emit({ pageName: toPageWrapper.pageName });
        } else { // If the destination page isn't registered
            // Check if the defaultError page is set, redirect to this one
            if (this._settings?.defaultErrorPage) {
                this.navigate(this._settings.defaultErrorPage);
            } else if (this._settings?.defaultPage) { // Else redirect to defaultPage
                this.navigate(this._settings.defaultPage);
            }
        }
    }

    /**
     * Dispatch a lifeCycle event to elements of app
     * 
     * @param listeningPage - Page key from where the event was triggered
     * @param navigationLifeCycle - LifeCycle to dispatch
     * @param pageNameAfterNavigation Page destination of navigate
     */
    protected _dispatchLifeCycleEvent(listeningPage: PageName, navigationLifeCycle: ENavigationLifeCycle, pageNameAfterNavigation?: PageName): void {

        const lifeCycleEvent: LifeCycleEvent = this._buildLifeCycleEvent(listeningPage, navigationLifeCycle, pageNameAfterNavigation);

        // Dispatch event to the page
        this._dispatchLifeCycleEventToPage(this._getPageInstance(listeningPage), navigationLifeCycle, lifeCycleEvent);
    }

    /**
     * Dispatch a lifeCycle event to page concerned by the event
     *
     * @param instance - Instance of class to trigger eventHandler, should extend NavigationListeners
     * @param navigationLifeCycle - LifeCycle to dispatch
     * @param lifeCycleEvent - Event to be dispatched
     */
    protected _dispatchLifeCycleEventToPage(instance: NavigationListeners | undefined, navigationLifeCycle: ENavigationLifeCycle, lifeCycleEvent: LifeCycleEvent): void {

        const eventHandler = instance?.[navigationLifeCycle];
        // Check if the page instance exist, and the function inside this class, if ok: call it
        if (eventHandler) {
            eventHandler.emit(lifeCycleEvent);
        }
    }

    /**
     * Load a chunck for a page from his import promise
     *
     * @param pageWrapper - Page to load
     */
    private _loadPage(pageWrapper: PageWrapper<PageName>): Promise<void> {
        if (!this._settings) {
            throw new Error("NavigationService configure was not called");
        }

        return pageWrapper.importPromise()
            .then(pageInstance => {
                // Set page instance of pageWrapper
                pageWrapper.pageInstance = pageInstance;
                return pageInstance.getHtmlElement();
            })
            .then((htmlElement: HTMLElement) => { // Build the page, retrieve the Html object
                pageWrapper.htmlElement = htmlElement;

                if (!this._navigationContainerHtml && this._settings) {
                    // Retrieve the html container in DOM
                    this._navigationContainerHtml = getElementByRef(document.body, this._settings.navigationContainerRefKey);
                }

                // Append page content into navigation container
                this._navigationContainerHtml?.append(pageWrapper.htmlElement);

                // Dispatch beforePageShow (page hasn't been loaded yet, so performNavigation hasn't triggered lifeCycleEvent for this page)
                this.beforePageShow.emit({ pageName: pageWrapper.pageName });
                this._dispatchLifeCycleEvent(pageWrapper.pageName, ENavigationLifeCycle.BeforePageShow, pageWrapper.pageName);

                // Display the page (remove display:none on pageWrapper.htmlElement)
                this._showPage(pageWrapper);

                // Dispatch afterPageShow (page hasn't been loaded yet, so performNavigation hasn't triggered lifeCycleEvent for this page)
                this._dispatchLifeCycleEvent(pageWrapper.pageName, ENavigationLifeCycle.AfterPageShow, pageWrapper.pageName);
                this.afterPageShow.emit({ pageName: pageWrapper.pageName });
            }).catch(this._log.error);
    }

    /**
     * Show default page on app init
     */
    protected _displayInitPage(): void {

        if (this._settings?.defaultPage) {
            // Check if the location hash exist and is referring to a registered page
            if (String(this.currentHash) !== "") {
                this._performNavigation(this.currentHash); // Else show the default page
            } else {
                this._performNavigation(this._settings?.defaultPage); // Else show the default page
            }
        } else {
            this._log.error("Default page should be set when installing NavigationService");
        }
    }

    /**
     * Factory of a LifeCycleEvent
     * @param pageName - The page name for which the event is triggered
     * @param navigationLifeCycle - The LifeCycle for which the event is triggered
     * @param pageNameAfterNavigation - The hashChange event himself
     * @returns Object builded
     */
    private _buildLifeCycleEvent(pageName: PageName, navigationLifeCycle: ENavigationLifeCycle, pageNameAfterNavigation?: PageName): LifeCycleEvent {
        return {
            pageName,
            navigationLifeCycle,
            pageNameAfterNavigation
        };
    }

    /**
     * Convert a string to a PageName object
     */
    protected _asPageName(str: string): PageName {
        return str as unknown as PageName;
    }

    /**
     * Convert a PageDefinitions to PageWrapper array
     */
    private _wrapPages(pageDefinitions: PageDefinitions): PageWrapper<PageName>[] {
        const pageWrappers: PageWrapper<PageName>[] = [];

        for (const key of Object.keys(pageDefinitions)) {
            pageWrappers.push({
                pageName: this._asPageName(key),

                importPromise: pageDefinitions[key].importPromise
            });
        }
        return pageWrappers;
    }

    /** @inheritdoc */
    public toString(): string {
        return `
        [NavigationService]
            | defaultPage: ${this._settings?.defaultPage}
            | defaultErrorPage: ${this._settings?.defaultErrorPage}
            | currentPage: ${this.currentPage}
            | navigationPages: ${CollectionTools.getMapKeys(this._navigationPages).forEach(page => "\n" + page)}
            | navigationPageNamesList: ${this.pageNames}
            | navigationContainerRefKey: ${this._settings?.navigationContainerRefKey}
            | queryParams: ${this.queryParams}
            | currentHash: ${this.currentHash}
            | currentPage: ${this.currentPage}
        `;
    }

    /** @inheritdoc */
    public toStringJSON(): string {
        return JSON.stringify(this);
    }

    /** @inheritdoc */
    public rebuildAppPagesFromPermissions(): void {

        if (!this._settings) {
            throw new Error("Can't rebuild appPages without settings.");
        }
        const currentPage = this.currentPage;
        if (!currentPage) {
            throw new Error("You can't rebuild app without a current page.");
        }

        // We don't need to refresh currentPageName and defaultPageName
        const pagesToKeep = [currentPage];
        if (this._settings.defaultPage && currentPage !== this._settings.defaultPage) {
            pagesToKeep.push(this._settings.defaultPage);
        }

        const pages = this.filterPagesFromPermissions({ ...this._settings.allConfigPages });
        // We check that currentPage is in the new list of page (means that user that still can see this page)
        if (!(currentPage in pages)) {
            throw new Error("User can not access to current page after fetch permissions.");
        }


        this._clearNavigationPages(pagesToKeep);

        // Remove pages we want to keep (we don't want menuHelper to add/remove them)
        for (const page of pagesToKeep) {
            delete pages[page];
        }

        const allowedPages: PageDefinitions = pages;
        const wrappers = this._wrapPages(allowedPages);
        wrappers.forEach((pageWrapper: PageWrapper<PageName>) => {
            this._addPage(pageWrapper);
        });

        // insert pages in menus
        if (this._settings?.menuHelper) {
            this._settings.menuHelper.setupMenus(this, this._log, allowedPages, true);
        }
    }

    /**
     * Clear navigation page expect currentPageName
     */
    private _clearNavigationPages(pageToKeep: PageName[] = []): void {
        this._navigationPages.forEach((pageWrapper, key) => {
            if (!pageToKeep.includes(pageWrapper.pageName)) {
                pageWrapper.htmlElement?.remove();
                this._navigationPages.delete(key);
            }
        });
    }
}

